import { readFirstSteps, type FirstSteps } from '~/utils/firstSteps'

/**
 * ⏩ v7.42 (per Javi: the banner's ⏩ button "will make things wonky when more
 * gold/help etc." - "what if instead we had that as an option in the hamburger
 * menu: Enable Tap to Fast Forward"): whether a tap on empty forest toggles ×3.
 *
 * A device preference (a wipe keeps it - progressEpoch PRESERVED_KEYS). Unset,
 * it follows the device: ON for anyone who played before (the v5.25 tap they
 * know), OFF on a device the title found new (first-steps `newcomer`, or new
 * but its account unheard - `provisional`) - an accidental ×3 was the #1
 * killer of first runs (F02). The menu flips it.
 *
 * v7.42 (live check): the default reads the title's LIVE first-steps state -
 * read once at app start it was ON for a newcomer's whole first visit (the
 * menu mounts before the title has marked the device new).
 */
export const TAP_FF_KEY = 'elemental-dispatch-tap-ff-v1'

type Store = Pick<Storage, 'getItem'> | null | undefined

/** The menu's explicit choice on this device, or null (unset: the default). */
export function readTapFastForwardChoice(store: Store): boolean | null {
  try {
    const saved = store?.getItem(TAP_FF_KEY)
    if (saved === '1') return true
    if (saved === '0') return false
  } catch { /* no storage: the default */ }
  return null
}

/** Unset, a newcomer device (or a new one whose account is unheard) starts OFF. */
export function tapFastForwardDefault(steps: FirstSteps, provisional: boolean): boolean {
  return !(steps.newcomer === true || provisional)
}

export function useTapFastForward() {
  const choice = useState<boolean | null>('tap-fast-forward', () => {
    try { return readTapFastForwardChoice(localStorage) } catch { return null }
  })
  // The same shared state useFirstSteps keeps (the title's decision writes it).
  const steps = useState<FirstSteps>('first-steps', () => {
    try { return readFirstSteps(localStorage) } catch { return {} }
  })
  const provisional = useState<boolean>('first-steps-provisional', () => false)
  const enabled = computed(() => choice.value ?? tapFastForwardDefault(steps.value, provisional.value))
  function setEnabled(on: boolean) {
    choice.value = on
    try { localStorage.setItem(TAP_FF_KEY, on ? '1' : '0') } catch { /* this session only */ }
  }
  return { enabled, setEnabled }
}
